// src/pages/ClubForumCategory.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { getClubForumCategoryBySlug } from '@/lib/supabase/forum';
import { ForumTopicPreview } from '@/components/forums/ForumTopicPreview';
import TopicListSkeleton from '@/components/forums/TopicListSkeleton';

interface ClubTopicRow {
  id: string;
  title: string;
  content: string;
  created_at: string;
  views_count: number;
  replies_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  last_reply_at: string | null;
  author: {
    id: string;          // internal user ID
    auth_id: string;
    full_name: string;
    avatar_url: string | null;
    city?: string;
  };
  last_reply_author: {
    id: string;          // internal user ID
    auth_id: string;
    full_name: string;
    avatar_url: string | null;
    city?: string;
  } | null;
}

const ClubForumCategory: React.FC = () => {
  const { slug, clubId } = useParams<{ slug: string; clubId: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleBack = () => {
    if (clubId) {
      navigate(`/dashboard/clubs/${clubId}`, {
        state: { activeTab: 'forum' },
        replace: true,
      });
    } else {
      navigate('/dashboard/forums');
    }
  };

  const { user } = useAuth();

  const [topics, setTopics] = useState<ClubTopicRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [categoryEmoji, setCategoryEmoji] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [categoryDescription, setCategoryDescription] = useState<string>(
    'Browse and discuss topics in this category'
  );
  const [clubLogo, setClubLogo] = useState<string | null>(null);
  const [clubName, setClubName] = useState<string>('');
  const [isMember, setIsMember] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  useEffect(() => {
    const fetchTopics = async () => {
      if (!slug || !clubId) return;

      setIsLoading(true);
      setError(null);

      try {
        // Fetch club info
        const { data: clubData, error: clubError } = await supabase
          .from('clubs')
          .select('club_name, logo')
          .eq('club_id', clubId)
          .single();

        if (clubError) throw clubError;
        setClubLogo(clubData.logo);
        setClubName(clubData.club_name);

        // Determine membership and admin status
        let isMemberTemp = false;
        let isAdminTemp = false;

        if (user) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id')
            .eq('auth_id', user.id)
            .single();
          if (userError) throw userError;

          const { data: membership, error: membershipError } = await supabase
            .from('user_clubs')
            .select('is_admin')
            .eq('user_id', userData.id)
            .eq('club_id', clubId)
            .maybeSingle();

          if (membership) {
            isMemberTemp = true;
            isAdminTemp = membership.is_admin;
          }
        }

        setIsMember(isMemberTemp);
        setIsAdmin(isAdminTemp);

        // Fetch category using visibility logic
        const category = await getClubForumCategoryBySlug(
          slug,
          clubId,
          isMemberTemp,
          isAdminTemp
        );

        if (!category) {
          setError('Category not found or access denied');
          return;
        }

        setCategoryName(category.name);
        setCategoryEmoji(category.emoji || null);
        setCategoryId(category.id);
        setCategoryDescription(
          category.description || 'Browse and discuss topics in this category'
        );

        // Fetch topics
        const { data, error: topicsError } = await supabase
          .from<ClubTopicRow>('club_forum_topics')
          .select(`
            id,
            title,
            content,
            created_at,
            views_count,
            replies_count,
            is_pinned,
            is_locked,
            last_reply_at,
            author:author_id(id, auth_id, full_name, avatar_url, city),
            last_reply_author:last_reply_author_id(id, auth_id, full_name, avatar_url)
          `)
          .eq('club_id', clubId)
          .eq('category_id', category.id)
          .order('is_pinned', { ascending: false })
          .order('last_reply_at', { ascending: false });

        if (topicsError) throw topicsError;
        setTopics(data || []);
      } catch (err) {
        console.error('Error loading forum category:', err);
        setError('Failed to load category or topics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopics();
  }, [slug, clubId, user]);

  const handleNewTopic = () => {
    navigate(
      `/dashboard/clubs/${clubId}/forum/new-topic?club=${clubId}` +
        (categoryId ? `&category=${categoryId}` : '')
    );
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="flex items-center text-dark-800 hover:text-white transition-colors mb-4"
          aria-label="Back to forum categories"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back
        </button>

        <div
          className={cn(
            'pb-4 border-b',
            isDarkMode ? 'border-dark-300' : 'border-light-300'
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-white flex items-center">
                {categoryEmoji && <span className="mr-2">{categoryEmoji}</span>}
                {categoryName || 'Forum Category'}
              </h1>
            </div>

            {clubLogo && (
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-dark-300 flex-shrink-0">
                  <img
                    src={clubLogo}
                    alt={clubName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        'https://hebbkx1anhila5yf.public.blob.vercel-storage.com/gold_symbol-removebg-n5agUlyNamyXXN0FU8QGzScjug8DVS.png';
                    }}
                  />
                </div>
                <span className="ml-3 text-white font-medium hidden sm:block">
                  {clubName}
                </span>
              </div>
            )}
          </div>

          <p
            className={cn(
              'text-dark-800 mt-1 whitespace-pre-line',
              !isDescriptionExpanded && categoryDescription.length > 120 && 'line-clamp-2'
            )}
            id="category-description"
          >
            {categoryDescription}
          </p>

          {categoryDescription.length > 120 && (
            <button
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              className="text-gold-400 hover:text-gold-500 text-sm mt-1 flex items-center"
            >
              {isDescriptionExpanded ? 'Show less' : 'Read more'}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={cn(
                  'h-3 w-3 ml-1 transition-transform duration-200',
                  isDescriptionExpanded && 'transform rotate-180'
                )}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          )}
        </div>

        <div className="flex justify-between items-center">
          <h2
            className={cn(
              'text-xl font-semibold',
              isDarkMode ? 'text-white' : 'text-light-900'
            )}
          >
            Topics
          </h2>
          <Button
            onClick={handleNewTopic}
            className="bg-gold-400 hover:bg-gold-500 text-dark-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Topic
          </Button>
        </div>

        <div className="space-y-4 mt-6">
          {isLoading ? (
            <TopicListSkeleton count={5} />
          ) : error ? (
            <div
              className={cn(
                'text-center py-12 rounded-xl border',
                isDarkMode ? 'bg-dark-300 border-dark-400' : 'bg-light-200 border-light-300'
              )}
            >
              <p className="text-red-500 mb-4">{error}</p>
              <Button
                onClick={() => window.location.reload()}
                className="text-sm underline hover:text-red-400"
              >
                Try Again
              </Button>
            </div>
          ) : topics.length > 0 ? (
            topics.map((topic) => (
              <ForumTopicPreview
                key={topic.id}
                topic={{
                  id: topic.id,
                  title: topic.title,
                  content: topic.content,
                  views_count: topic.views_count,
                  replies_count: topic.replies_count,
                  is_pinned: topic.is_pinned,
                  created_at: topic.created_at,
                  last_reply_at: topic.last_reply_at || topic.created_at,
                  author: {
                    id: topic.author.id,
                    full_name: topic.author.full_name,
                    avatar_url: topic.author.avatar_url || undefined,
                  },
                  last_reply_author:
                    topic.last_reply_author && {
                      id: topic.last_reply_author.id,
                      full_name: topic.last_reply_author.full_name,
                      avatar_url: topic.last_reply_author.avatar_url || undefined,
                    },
                }}
                onClick={() =>
                  navigate(`/dashboard/clubs/${clubId}/forum/topic/${topic.id}`)
                }
                onDeleted={(id) => setTopics((ts) => ts.filter((t) => t.id !== id))}
                onUpdated={({ id, title, content }) =>
                  setTopics((ts) =>
                    ts.map((t) =>
                      t.id === id ? { ...t, title, content: content || t.content } : t
                    )
                  )
                }
              />
            ))
          ) : (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 mx-auto text-dark-800 mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">No Topics Yet</h3>
              <p className="text-dark-800 mb-6">
                Be the first to start a topic in this category!
              </p>
              <Button
                onClick={handleNewTopic}
                className="bg-gold-400 hover:bg-gold-500 text-dark-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Start a New Topic
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClubForumCategory;
